<template>
  <div class="relative">
    <button
      ref="trigger"
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? listboxId : undefined"
      :aria-label="ariaLabel"
      :disabled="disabled"
      class="flex w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left focus:border-primary-500 focus:outline-none"
      :class="disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-400'"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span
        class="min-w-0 flex-1 truncate"
        :class="selected ? 'text-gray-900' : 'text-gray-500'"
      >
        {{ selected ? selected.label : placeholder }}
      </span>
      <FontAwesomeIcon
        :icon="['fas', 'chevron-down']"
        class="shrink-0 text-xs text-gray-500 transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menu"
        class="z-50 flex w-max max-w-lg flex-col rounded-lg border border-gray-200 bg-white shadow-lg"
        :style="menuStyle"
      >
        <div class="border-b border-gray-100 p-2">
          <div class="relative">
            <FontAwesomeIcon
              :icon="['fas', 'magnifying-glass']"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400"
            />
            <input
              ref="search"
              v-model="query"
              type="text"
              role="searchbox"
              :aria-controls="listboxId"
              :aria-activedescendant="focusedIndex >= 0 ? optionId(focusedIndex) : undefined"
              :placeholder="searchPlaceholder"
              class="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 focus:border-primary-500 focus:outline-none"
              @keydown="onSearchKeydown"
            >
          </div>
        </div>
        <div class="relative flex min-h-0 flex-1 flex-col">
          <div v-if="moreAbove" class="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-white" />
          <div v-if="moreBelow" class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-white" />
          <div :id="listboxId" ref="list" role="listbox" class="scroll-list relative min-h-0 flex-1 overflow-y-auto py-1" @scroll="measureScroll">
            <div
              v-for="(row, i) in rows"
              :id="optionId(i)"
              :key="row.clear ? '__clear__' : row.value"
              role="option"
              :aria-selected="row.value === modelValue"
              class="cursor-pointer truncate px-3 py-1.5"
              :class="[
                row.value === modelValue ? 'bg-primary-50 font-medium text-primary-700' : (row.clear ? 'text-gray-500' : 'text-gray-700'),
                { 'bg-gray-100': i === focusedIndex && row.value !== modelValue },
              ]"
              :title="row.label"
              @mousedown.prevent
              @click="choose(row)"
              @mouseenter="focusedIndex = i"
            >
              <template v-for="(part, j) in segments(row.label)" :key="j"><strong v-if="part.match" class="font-bold">{{ part.text }}</strong><template v-else>{{ part.text }}</template></template>
            </div>
            <div v-if="!rows.length" class="px-3 py-4 text-center text-gray-500">No matches</div>
          </div>
        </div>
        <div class="border-t border-gray-100 px-3 py-1.5 text-gray-500">
          {{ query ? `${filtered.length} of ${options.length}` : `${options.length} options` }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';

let uid = 0;

export default {
  name: 'SearchableSelect',
  components: { FontAwesomeIcon },
  props: {
    modelValue: { type: [String, Number], default: null },
    options: { type: Array, required: true },
    placeholder: { type: String, default: 'Select…' },
    searchPlaceholder: { type: String, default: 'Search…' },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: null },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return { isOpen: false, query: '', focusedIndex: -1, moreAbove: false, moreBelow: false, menuStyle: { position: 'fixed', top: '0px', left: '0px' } };
  },
  computed: {
    listboxId() {
      return `searchable-select-${this._uid}`;
    },
    selected() {
      return this.options.find((o) => o.value === this.modelValue) || null;
    },
    terms() {
      return this.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    },
    filtered() {
      if (!this.terms.length) return this.options;
      return this.options.filter((o) => {
        const label = String(o.label).toLowerCase();
        return this.terms.every((t) => label.includes(t));
      });
    },
    rows() {
      const clear = this.clearable && !this.query.trim() ? [{ value: null, label: this.placeholder, clear: true }] : [];
      return [...clear, ...this.filtered];
    },
  },
  watch: {
    query() {
      this.focusedIndex = this.rows.length ? 0 : -1;
      this.$nextTick(() => {
        if (this.$refs.list) this.$refs.list.scrollTop = 0;
        this.measureScroll();
      });
    },
    focusedIndex(i) {
      if (!this.isOpen || i < 0 || this.revealPending) return;
      this.$nextTick(() => this.reveal(i));
    },
  },
  created() {
    this._uid = uid++;
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutside);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutside);
    this.stopPositioning();
  },
  methods: {
    segments(label) {
      const text = String(label);
      const lower = text.toLowerCase();
      const hit = new Array(text.length).fill(false);
      this.terms.forEach((t) => {
        for (let at = lower.indexOf(t); at !== -1; at = lower.indexOf(t, at + 1)) hit.fill(true, at, at + t.length);
      });
      const parts = [];
      for (let i = 0; i < text.length; i += 1) {
        const last = parts[parts.length - 1];
        if (last && last.match === hit[i]) last.text += text[i];
        else parts.push({ text: text[i], match: hit[i] });
      }
      return parts;
    },
    optionId(i) {
      return `${this.listboxId}-option-${i}`;
    },
    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    },
    open(seed = '') {
      if (this.disabled || this.isOpen) return;
      this.query = seed;
      this.revealPending = true;
      this.isOpen = true;
      this.$nextTick(() => {
        const current = this.rows.findIndex((r) => r.value === this.modelValue);
        this.focusedIndex = current >= 0 && !seed ? current : (this.rows.length ? 0 : -1);
        this.$refs.search.focus();
        this.stopAutoUpdate = autoUpdate(this.$refs.trigger, this.$refs.menu, this.position);
      });
    },
    close(refocus = false) {
      if (!this.isOpen) return;
      this.stopPositioning();
      this.isOpen = false;
      this.revealPending = false;
      this.query = '';
      this.focusedIndex = -1;
      if (refocus) this.$refs.trigger.focus();
    },
    stopPositioning() {
      if (this.stopAutoUpdate) this.stopAutoUpdate();
      this.stopAutoUpdate = null;
    },
    position() {
      const { trigger, menu } = this.$refs;
      if (!trigger || !menu) return;
      computePosition(trigger, menu, {
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
          offset(4),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply: ({ availableHeight, rects }) => {
              Object.assign(menu.style, { minWidth: `${rects.reference.width}px`, maxHeight: `${Math.min(availableHeight, 384)}px` });
            },
          }),
        ],
      }).then(({ x, y }) => {
        this.menuStyle = { position: 'fixed', top: `${y}px`, left: `${x}px` };
        if (this.revealPending) {
          this.revealPending = false;
          this.reveal(this.focusedIndex, true);
        }
        this.measureScroll();
      });
    },
    reveal(i, center = false) {
      const { list } = this.$refs;
      const el = list && document.getElementById(this.optionId(i));
      if (!el) return;
      const top = el.offsetTop;
      const bottom = top + el.offsetHeight;
      if (center) list.scrollTop = top - (list.clientHeight - el.offsetHeight) / 2;
      else if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
      this.measureScroll();
    },
    measureScroll() {
      const { list } = this.$refs;
      if (!list) return;
      this.moreAbove = list.scrollTop > 0;
      this.moreBelow = list.scrollTop + list.clientHeight < list.scrollHeight - 1;
    },
    choose(row) {
      this.$emit('update:modelValue', row.value);
      this.$emit('change', row.value);
      this.close(true);
    },
    move(delta) {
      if (!this.rows.length) return;
      this.focusedIndex = Math.min(Math.max(this.focusedIndex + delta, 0), this.rows.length - 1);
    },
    onTriggerKeydown(e) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        this.open();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        this.open(e.key);
      }
    },
    onSearchKeydown(e) {
      const keys = {
        ArrowDown: () => this.move(1),
        ArrowUp: () => this.move(-1),
        PageDown: () => this.move(10),
        PageUp: () => this.move(-10),
        Enter: () => this.rows[this.focusedIndex] && this.choose(this.rows[this.focusedIndex]),
        Escape: () => this.close(true),
      };
      if (e.key === 'Tab') {
        this.close(true);
        return;
      }
      if (!keys[e.key]) return;
      e.preventDefault();
      e.stopPropagation();
      keys[e.key]();
    },
    onOutside(e) {
      if (!this.isOpen) return;
      if (this.$el.contains(e.target) || this.$refs.menu?.contains(e.target)) return;
      this.close();
    },
  },
};
</script>

<style scoped>
.scroll-list::-webkit-scrollbar {
  width: 8px;
}
.scroll-list::-webkit-scrollbar-track {
  background: transparent;
}
.scroll-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}
.scroll-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
